/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Chip
} from "@mui/material"
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc } from "firebase/firestore"
import { toast } from 'react-toastify'
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import * as EmailValidator from 'email-validator'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

interface INotificationCategory {
  id?: string
  categoryName: string
  description: string
  emails: string[]
  createdAt?: string
  updatedAt?: string
}

const NotificationCategories = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const [items, setItems] = useState<Array<any>>([])
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentCategory, setCurrentCategory] = useState<INotificationCategory>({
    categoryName: '',
    description: '',
    emails: []
  })
  const [emailInput, setEmailInput] = useState<string>('')
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({})
  const categoriesRef = collection(db, 'notificationCategories')

  const columns = [
    {
      field: "No",
      headerName: "No",
      width: 70,
    },
    {
      field: "categoryName",
      headerName: "カテゴリ名",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "description",
      headerName: "説明",
      flex: 1.5,
    },
    {
      field: "emails",
      headerName: "登録メールアドレス",
      flex: 2,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 1 }}>
          {params.row.emails?.map((email: string, index: number) => (
            <Chip key={index} label={email} size="small" />
          ))}
        </Box>
      ),
    },
    {
      field: "updatedAt",
      headerName: "更新日時",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "操作",
      flex: 1,
      renderCell: (params: any) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleEdit(params.row)}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDelete(params.row.id)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    }
  ]

  useEffect(() => {
    const unsubscribe = onSnapshot(categoriesRef, ({ docs }) => {
      setItems(docs.map((doc, i) => {
        const data = doc.data()
        return ({
          No: i + 1,
          ...data,
          id: doc.id,
          emails: data.emails || []
        })
      }))
    })
    return unsubscribe
  }, [])

  const handleEdit = (category: any) => {
    setEditMode(true)
    setCurrentCategory({
      id: category.id,
      categoryName: category.categoryName,
      description: category.description,
      emails: category.emails || []
    })
    setOpenDialog(true)
  }

  const handleDelete = async (id: string) => {
    if(!window.confirm("このカテゴリを削除しますか？")) return
    try {
      await deleteDoc(doc(db, "notificationCategories", id))
      toast.success("カテゴリを削除しました")
    } catch (e) {
      toast.error("削除に失敗しました: " + JSON.stringify(e))
    }
  }

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim()
    if (!trimmedEmail) {
      toast.warning("メールアドレスを入力してください")
      return
    }
    if (!EmailValidator.validate(trimmedEmail)) {
      toast.error("メールアドレスが不正です")
      return
    }
    if (currentCategory.emails.includes(trimmedEmail)) {
      toast.warning("既に登録されているメールアドレスです")
      return
    }
    setCurrentCategory(prev => ({
      ...prev,
      emails: [...prev.emails, trimmedEmail]
    }))
    setEmailInput('')
  }

  const handleRemoveEmail = (index: number) => {
    setCurrentCategory(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }))
  }

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {}

    if (!currentCategory.categoryName.trim()) {
      errors.categoryName = 'カテゴリ名は必須です'
    }

    if (currentCategory.emails.length === 0) {
      errors.emails = '少なくとも1つのメールアドレスを登録してください'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    try {
      const timestamp = dayjs().format("YYYY/MM/DD HH:mm:ss")
      const categoryData = {
        categoryName: currentCategory.categoryName.trim(),
        description: currentCategory.description.trim(),
        emails: currentCategory.emails,
        updatedAt: timestamp
      }

      if (editMode && currentCategory.id) {
        await updateDoc(doc(db, "notificationCategories", currentCategory.id), categoryData)
        toast.success("カテゴリを更新しました")
      } else {
        await addDoc(categoriesRef, {
          ...categoryData,
          createdAt: timestamp
        })
        toast.success("新規カテゴリを追加しました")
      }

      handleCloseDialog()
    } catch (e) {
      toast.error("保存に失敗しました: " + JSON.stringify(e))
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditMode(false)
    setCurrentCategory({
      categoryName: '',
      description: '',
      emails: []
    })
    setEmailInput('')
    setFormErrors({})
  }

  const handleOpenNewDialog = () => {
    setEditMode(false)
    setCurrentCategory({
      categoryName: '',
      description: '',
      emails: []
    })
    setOpenDialog(true)
  }

  return (
    <Box m={5}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <h2>通知カテゴリ管理</h2>
          <p>カテゴリごとにメール通知先を管理します</p>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenNewDialog}
        >
          新規カテゴリ追加
        </Button>
      </Box>

      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={items}
          columns={columns}
          getRowHeight={() => 'auto'}
        />
      </Box>

      {/* カテゴリ追加/編集モーダル */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'カテゴリ編集' : '新規カテゴリ追加'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="カテゴリ名 *"
                value={currentCategory.categoryName}
                onChange={(e) => {
                  setCurrentCategory(prev => ({ ...prev, categoryName: e.target.value }))
                  if (formErrors.categoryName) {
                    setFormErrors(prev => ({ ...prev, categoryName: '' }))
                  }
                }}
                error={!!formErrors.categoryName}
                helperText={formErrors.categoryName}
                placeholder="例: 個体到着通知"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="説明"
                value={currentCategory.description}
                onChange={(e) => setCurrentCategory(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={2}
                placeholder="このカテゴリの説明を入力してください"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    fullWidth
                    label="メールアドレス追加"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddEmail()
                      }
                    }}
                    placeholder="example@example.com"
                    error={!!formErrors.emails}
                    helperText={formErrors.emails}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddEmail}
                    sx={{ minWidth: '100px' }}
                  >
                    追加
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {currentCategory.emails.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => handleRemoveEmail(index)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                {currentCategory.emails.length === 0 && (
                  <Box sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                    メールアドレスが登録されていません
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            キャンセル
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editMode ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default NotificationCategories
